import {
    IonButton,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSegment,
    IonSegmentButton,
    IonTitle,
    IonToolbar,
    RefresherEventDetail,
    useIonRouter
} from "@ionic/react";
import {
    arrowForwardOutline,
    calendarOutline,
    closeOutline,
    americanFootballOutline,
    chevronBackOutline,
    chevronForwardOutline,
    peopleOutline,
    refreshOutline,
    settingsOutline,
    timeOutline,
    trophyOutline
} from "ionicons/icons";
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import {
    useLocation,
    useParams
} from "react-router-dom";
import {
    FantasyMainTab,
    getFantasyMainTab,
    getFantasyMainTabPath
} from "../utils/fantasyNavigation";
import FantasyHeaderActions from "../components/FantasyHeaderActions";
import {
    isAuthenticated
} from "../services/auth";

import {
    fantasyApiGet
} from "../services/fantasyApi";

import "./FantasyLeague.css";
import "./FantasyNavigation.css";


interface FantasyLeagueInfo {
    id?: string;
    fantasy?: string;
    name?: string;
    password?: string;
    format?: string;
    time?: string;
    date?: string;
    hour?: string;
    draftStartAt?: string;
    order?: string;
    teams?: string;
    playoffs?: string;
    draftCompleted?: boolean | string | number;
    draftStatus?: "scheduled" | "active" | "completed";
}
interface FantasyPublicTeam {
    id: string;
    name: string;
    logo?: string;
    ownerName?: string;
}

interface FantasyStanding {
    rank: number;
    teamId: string;
    played: number;
    wins: number;
    losses: number;
    ties: number;
    byes: number;
    pointsFor: number;
    pointsAgainst: number;
    difference: number;

    playoffClinched?: boolean | null;
}

interface FantasyGamePlayer {
    id: string;
    name: string;
    points: number;
    isStarter: boolean;
    teamId?: string;
    positionId?: string;
    slot?: string;
}


interface FantasyGameTeamResult {
    points: number;
    players: FantasyGamePlayer[];
}

interface FantasyGameResult {
    status: "final" | "bye";
    winnerId?: string;
    tieBreaker?: string;

    teams?: Record<string, FantasyGameTeamResult>;

    teamId?: string;
    points?: number;
    players?: FantasyGamePlayer[];
}



interface FantasyLivePlayer {
    id: string;
    name: string;
    teamId: string;
    positionId: string;
    slot: string;
    isStarter: boolean;
    points: number;
}

interface FantasyLiveTeamScore {
    teamId: string;
    points: number;
    starterPoints: number;
    benchPoints: number;
    players: FantasyLivePlayer[];
    snapshotCreatedAt?: string;
    snapshotCompletedAt?: string;
}

interface FantasyLiveMatch {
    id: string;
    status: "scheduled" | "live" | "final";
    isLocked: boolean;
    startAt: string;
    localTeamId: string;
    localTeamName: string;
    localScore: number;
    visitorTeamId: string;
    visitorTeamName: string;
    visitorScore: number;
}

interface FantasyLiveRound {
    realRound: number;
    fantasyRound: number;
    updatedAt: string;
    matches: FantasyLiveMatch[];
    teams: Record<string, FantasyLiveTeamScore>;
}

interface FantasyGame {
    id: string;
    jornada: number;

    phase?: "regular" | "playoff" | string;

    stage?:
        | "semifinal"
        | "final"
        | "third-place"
        | "placement"
        | string;

    bracket?: "championship" | "consolation" | string;

    seedHome?: number | null;
    seedAway?: number | null;

    placementRange?: string;
    sourceGameIds?: string[];

    [key: `team${number}`]: string | undefined;

    bye?: boolean;
    result?: FantasyGameResult;
}

interface FantasyLeagueActivityTransaction {
    id: string;

    type:
        | "add"
        | "replace"
        | "drop"
        | "waiver_add"
        | "waiver_replace"
        | "trade"
        | string;

    teamId: string;
    teamName: string;

    toTeamId?: string;
    toTeamName?: string;

    addedPlayerId?: string;
    addedPlayerName?: string;

    droppedPlayerId?: string;
    droppedPlayerName?: string;

    sentPlayerNames?: string[];
    receivedPlayerNames?: string[];

    realRound: number;
    fantasyRound: number;
    createdAt: string;
}

interface FantasyLeagueActivity {
    fantasyRound: number;
    transactions: FantasyLeagueActivityTransaction[];
}

interface FantasyLeagueState {
    success: boolean;
    serverNow: string;
    info: FantasyLeagueInfo;

    league: {
        id: string;
        name: string;
        competitionId: string;
        currentUserTeamId: string;
    };

    week?: {
        realRound: number;
        fantasyRound: number;
    };

    liveRound?: FantasyLiveRound | null;

    teams: Record<string, FantasyPublicTeam>;
    games: Record<string, FantasyGame>;
    standings: Record<string, FantasyStanding>;
    draftOrder: string[];

    activity?: FantasyLeagueActivity;
}

function isDraftCompleted(
    info: FantasyLeagueInfo | null
): boolean {
    const value = info?.draftCompleted;

    if (
        value === true ||
        value === "true" ||
        value === "1" ||
        value === 1
    ) {
        return true;
    }

    return (
        String(info?.draftStatus || "")
            .trim()
            .toLowerCase() === "completed"
    );
}

function getGameTeamIds(
    game: FantasyGame
): string[] {
    return Object.entries(game)
        .filter(function ([key, value]) {
            return (
                /^team[0-9]+$/.test(key) &&
                typeof value === "string" &&
                value.trim() !== ""
            );
        })
        .sort(function ([leftKey], [rightKey]) {
            return leftKey.localeCompare(
                rightKey,
                undefined,
                {
                    numeric: true
                }
            );
        })
        .map(function ([, value]) {
            return value as string;
        });
}

function getLeagueInfo(data: unknown): FantasyLeagueInfo | null {
    if (!data || typeof data !== "object") {
        return null;
    }

    const rootData = data as {
        Info?: FantasyLeagueInfo;
    };

    /*
        Estructura esperada:
        {
            "Info": {
                "name": "Prueba",
                "date": "2026-08-27",
                "hour": "14"
            }
        }
    */
    if (rootData.Info && typeof rootData.Info === "object") {
        return rootData.Info;
    }

    /*
        Compatibilidad con archivos que puedan tener un nivel adicional.
    */
    const firstLevel = Object.values(data).find(function (item) {
        return (
            item &&
            typeof item === "object" &&
            "Info" in item
        );
    }) as { Info?: FantasyLeagueInfo } | undefined;

    return firstLevel?.Info || null;
}

function getLeagueName(info: FantasyLeagueInfo | null): string {
    const name = String(info?.name || "").trim();

    return name || "Liga Fantasy";
}

function getDraftDate(
    info: FantasyLeagueInfo | null
): Date | null {
    const draftStartAt = String(
        info?.draftStartAt || ""
    ).trim();

    if (!draftStartAt) {
        return null;
    }

    const draftDate = new Date(draftStartAt);

    return Number.isNaN(draftDate.getTime())
        ? null
        : draftDate;
}

function formatDraftDate(date: Date): string {
    return new Intl.DateTimeFormat("es-MX", {
        timeZone: "America/Monterrey",
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    }).format(date);
}

function formatCountdown(milliseconds: number): string {
    const totalSeconds = Math.max(
        0,
        Math.floor(milliseconds / 1000)
    );

    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const paddedHours = String(hours).padStart(2, "0");
    const paddedMinutes = String(minutes).padStart(2, "0");
    const paddedSeconds = String(seconds).padStart(2, "0");

    if (days > 0) {
        return `${days}d ${paddedHours}h ${paddedMinutes}m ${paddedSeconds}s`;
    }

    return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
}

function formatActivityDate(value: string): string {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return "";
    }

    return new Intl.DateTimeFormat("es-MX", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit"
    }).format(date);
}

function getActivityTypeLabel(
    type: string
): string {
    switch (type) {
        case "waiver_add":
        case "waiver_replace":
            return "WAIVER";

        case "trade":
            return "TRADE";

        case "drop":
            return "LIBERADO";

        case "add":
        case "replace":
            return "AGENCIA LIBRE";

        default:
            return "MOVIMIENTO";
    }
}

function getActivityDescription(
    transaction: FantasyLeagueActivityTransaction
): string {
    const teamName =
        transaction.teamName || "Un equipo";

    const addedPlayer =
        transaction.addedPlayerName || "un jugador";

    const droppedPlayer =
        transaction.droppedPlayerName || "un jugador";

    switch (transaction.type) {
        case "waiver_add":
            return `${teamName} obtuvo a ${addedPlayer} por waivers.`;

        case "waiver_replace":
            return `${teamName} obtuvo a ${addedPlayer} por waivers y liberó a ${droppedPlayer}.`;

        case "add":
            return `${teamName} agregó a ${addedPlayer}.`;

        case "replace":
            return `${teamName} agregó a ${addedPlayer} y liberó a ${droppedPlayer}.`;

        case "drop":
            return `${teamName} liberó a ${droppedPlayer}.`;

        case "trade": {
            const destination =
                transaction.toTeamName || "otro equipo";

            const sent =
                transaction.sentPlayerNames?.join(", ") ||
                "jugadores";

            const received =
                transaction.receivedPlayerNames?.join(", ") ||
                "jugadores";

            return `${teamName} intercambió ${sent} con ${destination} por ${received}.`;
        }

        default:
            return `${teamName} realizó un movimiento.`;
    }
}

function isPlayoffGame(game: FantasyGame): boolean {
    return String(game.phase || "")
        .trim()
        .toLowerCase() === "playoff";
}

function getPositionLabel(position: number): string {
    switch (position) {
        case 1:
            return "1ER LUGAR";

        case 2:
            return "2DO LUGAR";

        case 3:
            return "3ER LUGAR";

        case 4:
            return "4TO LUGAR";

        case 5:
            return "5TO LUGAR";

        case 6:
            return "6TO LUGAR";

        case 7:
            return "7MO LUGAR";

        case 8:
            return "8VO LUGAR";

        default:
            return `${position}° LUGAR`;
    }
}

function getPlacementWinnerPosition(
    placementRange?: string
): number | null {
    const match = String(placementRange || "")
        .trim()
        .match(/^(\d+)-(\d+)$/);

    if (!match) {
        return null;
    }

    const position = Number(match[1]);

    return Number.isInteger(position) && position > 0
        ? position
        : null;
}

function getPlayoffGameLabel(game: FantasyGame): string {
    if (!isPlayoffGame(game)) {
        return "";
    }

    switch (String(game.stage || "").toLowerCase()) {
        case "semifinal":
            return "SEMIFINAL";

        case "final":
            return "FINAL · 1ER LUGAR";

        case "third-place":
            return "JUEGO POR EL 3ER LUGAR";

        case "placement": {
            const position = getPlacementWinnerPosition(
                game.placementRange
            );

            return position
                ? `JUEGO POR EL ${getPositionLabel(position)}`
                : "JUEGO DE POSICIÓN";
        }

        default:
            return "PLAYOFFS";
    }
}

function getWinnerTagLabel(game: FantasyGame): string {
    if (!isPlayoffGame(game)) {
        return "GANADOR";
    }

    switch (String(game.stage || "").toLowerCase()) {
        case "final":
            return "CAMPEÓN";

        case "third-place":
            return "3ER LUGAR";

        case "placement": {
            const position = getPlacementWinnerPosition(
                game.placementRange
            );

            return position
                ? getPositionLabel(position)
                : "GANADOR";
        }

        default:
            /*
                En semifinales se mantiene como ganador porque
                el equipo todavía avanza a otra ronda.
            */
            return "GANADOR";
    }
}

function getTieBreakerLabel(value?: string): string {
    switch (value) {
        case "benchPoints":
            return "puntos de banca";

        case "bestPlayer":
            return "mejor jugador";

        case "higherSeed":
            return "mejor seed";

        default:
            return "empate exacto";
    }
}

const FantasyLeague: React.FC = () => {
    const router = useIonRouter();
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
const activeMainTab = useMemo(
    function (): FantasyMainTab | undefined {
        return getFantasyMainTab(
            location.pathname,
            id
        );
    },
    [id, location.pathname]
);

function navigateMainTab(
    selectedTab: FantasyMainTab
): void {
    if (!id || selectedTab === activeMainTab) {
        return;
    }

    router.push(
        getFantasyMainTabPath(
            selectedTab,
            id
        ),
        "none",
        "replace"
    );
}

    const [leagueName, setLeagueName] = useState("Liga Fantasy");
    const [leagueInfo, setLeagueInfo] =
    useState<FantasyLeagueInfo | null>(null);

    const [selectedCalendarRound, setSelectedCalendarRound] =
    useState<number | null>(null);

const [draftDate, setDraftDate] =
    useState<Date | null>(null);

const serverClockRef = useRef({
    serverTime: 0,
    receivedAt: 0
});

const [clockTick, setClockTick] = useState(
    function () {
        return performance.now();
    }
);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [fantasyState, setFantasyState] =
    useState<FantasyLeagueState | null>(null);
    const latestLoadRequestRef = useRef(0);
const pollingRequestRef = useRef(false);

function synchronizeServerClock(
    serverNow: string
) {
    const serverTime = new Date(serverNow).getTime();

    if (Number.isNaN(serverTime)) {
        return;
    }

    serverClockRef.current = {
        serverTime,
        receivedAt: performance.now()
    };

    /*
        Fuerza una actualización visual inmediata del contador.
    */
    setClockTick(performance.now());
}

function getEstimatedServerTime(): number {
    const clock = serverClockRef.current;

    /*
        Sólo es respaldo antes de que el endpoint responda
        por primera vez con la hora oficial.
    */
    if (clock.serverTime === 0) {
        return Date.now();
    }

    return (
        clock.serverTime +
        (performance.now() - clock.receivedAt)
    );
}

    const loadLeague = useCallback(
    async function (forceRefresh = false) {
        if (!isAuthenticated()) {
            router.push("/home", "root", "replace");
            return;
        }

        const requestId =
            latestLoadRequestRef.current + 1;

        latestLoadRequestRef.current = requestId;

        try {
            setError("");

            if (!forceRefresh) {
                setLoading(true);
            }

const result = await fantasyApiGet<
    FantasyLeagueState
>(
    "control_GetFantasyLeague.php" +
        `?id=${encodeURIComponent(id)}` +
        `&t=${Date.now()}`
);

            /*
                Evita que una respuesta vieja reemplace datos
                más recientes obtenidos por pull-to-refresh o polling.
            */
            if (
                requestId !==
                latestLoadRequestRef.current
            ) {
                return;
            }

            synchronizeServerClock(
    result.serverNow
);

setFantasyState(result);

setLeagueInfo(result.info);

            setLeagueName(
                getLeagueName(result.info)
            );
            setDraftDate(
                getDraftDate(result.info)
            );
        } catch (requestError) {
            if (
                requestId !==
                latestLoadRequestRef.current
            ) {
                return;
            }

            console.error(
                "No fue posible cargar la liga Fantasy:",
                requestError
            );

            setError(
                requestError instanceof Error
                    ? requestError.message
                    : "No fue posible cargar la información de esta liga."
            );
        } finally {
            if (
                requestId ===
                latestLoadRequestRef.current
            ) {
                setLoading(false);
            }
        }
    },
    [id, router]
);

    useEffect(
        function () {
            loadLeague();
        },
        [loadLeague]
    );
    useEffect(
    function () {
        async function refreshLeagueState() {
            if (pollingRequestRef.current) {
                return;
            }

            if (
                document.visibilityState === "hidden"
            ) {
                return;
            }

            pollingRequestRef.current = true;

            try {
                await loadLeague(true);
            } finally {
                pollingRequestRef.current = false;
            }
        }

        const intervalId = window.setInterval(
            refreshLeagueState,
            15000
        );

        function handleVisibilityChange() {
            if (
                document.visibilityState === "visible"
            ) {
                refreshLeagueState();
            }
        }

        document.addEventListener(
            "visibilitychange",
            handleVisibilityChange
        );

        return function () {
            window.clearInterval(intervalId);

            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );
        };
    },
    [loadLeague]
);

useEffect(function () {
    const intervalId = window.setInterval(function () {
        /*
            performance.now() no cambia si el usuario modifica
            manualmente la fecha u hora de su dispositivo.
        */
        setClockTick(performance.now());
    }, 1000);

    return function () {
        window.clearInterval(intervalId);
    };
}, []);

useEffect(
    function () {
        setSelectedCalendarRound(null);
    },
    [id]
);

/*
    clockTick se usa para provocar un render cada segundo.
    La hora real se estima desde el último serverNow recibido.
*/
void clockTick;

const estimatedServerTime =
    getEstimatedServerTime();

const remainingDraftTime = draftDate
    ? draftDate.getTime() - estimatedServerTime
    : 0;

const isDraftPending =
    !isDraftCompleted(
        fantasyState?.info || leagueInfo
    ) &&
    draftDate !== null &&
    remainingDraftTime > 0;

const hasDraftFinished = isDraftCompleted(
    fantasyState?.info || leagueInfo
);

const games = Object.values(
    fantasyState?.games || {}
);

/*
    Jornada Fantasy activa definida por el servidor.
    Esta referencia no se modifica al navegar el calendario.
*/
const currentFantasyRound = Number.isInteger(
    fantasyState?.week?.fantasyRound
)
    ? Number(fantasyState?.week?.fantasyRound)
    : 0;

/*
    Jornadas disponibles en el calendario de la liga.
    Se ordenan de menor a mayor y se eliminan duplicados.
*/
const availableFantasyRounds = Array.from(
    new Set(
        games
            .map(function (game) {
                return game.jornada;
            })
            .filter(function (round) {
                return Number.isInteger(round) && round >= 0;
            })
    )
).sort(function (left, right) {
    return left - right;
});

/*
    selectedCalendarRound sólo cambia la vista.
    Si aún no se ha navegado manualmente, se muestra la jornada
    activa indicada por el servidor.
*/
const visibleRound =
    selectedCalendarRound ?? currentFantasyRound;

const visibleRoundIndex =
    availableFantasyRounds.indexOf(visibleRound);

const previousRound =
    visibleRoundIndex > 0
        ? availableFantasyRounds[visibleRoundIndex - 1]
        : null;

const nextRound =
    visibleRoundIndex >= 0 &&
    visibleRoundIndex <
        availableFantasyRounds.length - 1
        ? availableFantasyRounds[visibleRoundIndex + 1]
        : null;

const roundGames = games.filter(function (game) {
    return game.jornada === visibleRound;
});

const standingsByTeam = fantasyState?.standings || {};
const teamsById = fantasyState?.teams || {};

/*
    Se crea una posición base para cada equipo que no tenga
    todavía una entrada en standings.
*/
const standings = Object.keys(teamsById)
    .map(function (teamId) {
        return standingsByTeam[teamId] || {
            rank: Number.MAX_SAFE_INTEGER,
            teamId,
            played: 0,
            wins: 0,
            losses: 0,
            ties: 0,
            byes: 0,
            pointsFor: 0,
            pointsAgainst: 0,
            difference: 0
        };
    })
    .sort(function (left, right) {
        if (left.rank !== right.rank) {
            return left.rank - right.rank;
        }

        /*
            Respaldo visual; normalmente nunca se usa porque
            rank viene resuelto desde el backend.
        */
        return left.teamId.localeCompare(
            right.teamId,
            "es-MX"
        );
    });

const leagueActivity =
    fantasyState?.activity?.transactions || [];

const liveRound = fantasyState?.liveRound || null;

const isVisibleRoundLive =
    liveRound?.fantasyRound === visibleRound;

const hasLiveRealMatch = Boolean(
    liveRound?.matches.some(function (match) {
        return match.status === "live";
    })
);

/*
    La jornada ya inició si al menos un partido real está en vivo
    o ya finalizó. Esto mantiene visibles los puntos durante los
    espacios entre partidos programados.
*/
const hasStartedRealRound = Boolean(
    liveRound?.matches.some(function (match) {
        return (
            match.status === "live" ||
            match.status === "final"
        );
    })
);

const areAllRealMatchesFinal = Boolean(
    liveRound &&
        liveRound.matches.length > 0 &&
        liveRound.matches.every(function (match) {
            return match.status === "final";
        })
);

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadLeague(true);
        event.detail.complete();
    }

    function goToLeagueGame(): void {
    const from = encodeURIComponent(
        location.pathname + location.search
    );

    router.push(
        `/fantasyGame/${id}?from=${from}`,
        "none",
        "replace"
    );
}

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="fantasy-league-toolbar">
                    <IonButton
                        slot="start"
                        fill="clear"
                        aria-label="Salir de la liga"
                        onClick={function () {
                            router.push(
                                "/fantasy",
                                "root",
                                "replace"
                            );
                        }}
                    >
                        <IonIcon icon={closeOutline} />
                    </IonButton>
                    <IonTitle>Fantasy</IonTitle>

                    <FantasyHeaderActions
                        leagueId={id || ""}
                        screen="league"
                    />
                </IonToolbar>

<IonToolbar className="fantasy-navigation-toolbar">
    <IonSegment
        value={activeMainTab || ""}
        onIonChange={function (event) {
            const selectedTab = String(
                event.detail.value || ""
            ) as FantasyMainTab;

            if (
                ![
                    "home",
                    "players",
                    "team",
                    "game"
                ].includes(selectedTab)
            ) {
                return;
            }

            navigateMainTab(selectedTab);
        }}
    >
        <IonSegmentButton value="home">
            Inicio
        </IonSegmentButton>

        <IonSegmentButton value="players">
            Jugadores
        </IonSegmentButton>

        <IonSegmentButton value="team">
            Mi equipo
        </IonSegmentButton>

        <IonSegmentButton value="game">
            Juego
        </IonSegmentButton>
    </IonSegment>
</IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="fantasy-league-content">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando liga..."
                    />
                </IonRefresher>

                <section className="fantasy-league-hero">
                    <div className="fantasy-league-hero__icon">
                        <IonIcon icon={trophyOutline} />
                    </div>

                    <div>
                        <span>LIGA FANTASY</span>
                        <h1>{leagueName}</h1>
                        <p>
                            Administra tu equipo y sigue la actividad de tu
                            liga.
                        </p>
                    </div>
                </section>

                {loading && (
                    <IonProgressBar
                        className="fantasy-league-progress"
                        type="indeterminate"
                    />
                )}

                {!loading && error && (
                    <section className="fantasy-league-main">
                        <div className="fantasy-league-empty">
                            <IonIcon icon={refreshOutline} />
                            <strong>No pudimos cargar la liga</strong>
                            <span>{error}</span>

                            <IonButton
                                fill="outline"
                                size="small"
                                onClick={function () {
                                    loadLeague(true);
                                }}
                            >
                                Reintentar
                            </IonButton>
                        </div>
                    </section>
                )}

                {!loading && !error && (
                    <main className="fantasy-league-main">
                        {hasDraftFinished ? (
    <>
        <section className="fantasy-league-games">
<div className="fantasy-league-section__heading">
    <div>
        <span>
            JORNADA {visibleRound + 1}
            {visibleRound === currentFantasyRound
                ? " · ACTUAL"
                : ""}
        </span>

        <h2>Juegos de la semana</h2>
    </div>

    <div
        className="fantasy-league-round-navigation"
        aria-label="Navegación de jornadas"
    >
        <IonButton
            fill="clear"
            className="fantasy-league-round-navigation__button"
            aria-label="Ver jornada anterior"
            disabled={previousRound === null}
            onClick={function () {
                if (previousRound !== null) {
                    setSelectedCalendarRound(previousRound);
                }
            }}
        >
            <IonIcon icon={chevronBackOutline} />
        </IonButton>

        <IonButton
            fill="clear"
            className="fantasy-league-round-navigation__button"
            aria-label="Ver jornada siguiente"
            disabled={nextRound === null}
            onClick={function () {
                if (nextRound !== null) {
                    setSelectedCalendarRound(nextRound);
                }
            }}
        >
            <IonIcon icon={chevronForwardOutline} />
        </IonButton>
    </div>
</div>
            {roundGames.length === 0 ? (
                <div className="fantasy-league-notice">
                    <span className="fantasy-league-notice__icon">
                        <IonIcon icon={americanFootballOutline} />
                    </span>

                    <div>
                        <strong>No hay juegos disponibles</strong>

                        <p>
                            El calendario de esta jornada todavía no
                            está disponible.
                        </p>
                    </div>
                </div>
            ) : (
                <div className="fantasy-league-games__list">
                    {roundGames.map(function (game) {
                        const teamIds = getGameTeamIds(game);

                        const playoffGame = isPlayoffGame(game);

const playoffGameLabel = getPlayoffGameLabel(game);

const winnerTagLabel = getWinnerTagLabel(game);

                        const firstTeamId = teamIds[0] || "";
                        const secondTeamId = teamIds[1] || "";

                        const firstTeam =
                            fantasyState?.teams[firstTeamId];

                        const secondTeam =
                            fantasyState?.teams[secondTeamId];
const firstResult =
    game.result?.teams?.[firstTeamId];

const secondResult =
    game.result?.teams?.[secondTeamId];

const isFinal =
    game.result?.status === "final";

const isProcessedBye =
    game.result?.status === "bye";

const isBye =
    isProcessedBye ||
    game.bye === true;

/*
    liveRound sólo contiene los puntos de la jornada Fantasy
    actualmente activa. Nunca debe usarse al navegar a otra semana.
*/
const firstLiveScore = isVisibleRoundLive
    ? liveRound?.teams[firstTeamId]
    : undefined;

const secondLiveScore = isVisibleRoundLive
    ? liveRound?.teams[secondTeamId]
    : undefined;

/*
    Los puntos temporales se muestran desde que comienza el primer
    partido de la jornada y permanecen visibles hasta que la jornada
    sea procesada y consolidada como resultado Fantasy.
*/
const canShowLivePoints = Boolean(
    isVisibleRoundLive &&
    liveRound &&
    hasStartedRealRound
);

/*
    Los puntos de jugadores existen cuando:
    - el juego Fantasy ya finalizó;
    - es una semana bye procesada;
    - o se está consultando la jornada activa con puntos live.
*/
const canShowPlayerPoints =
    isFinal ||
    isProcessedBye ||
    canShowLivePoints;

const firstPoints = isProcessedBye
    ? game.result?.points ?? 0
    : isFinal
        ? firstResult?.points ?? 0
        : canShowLivePoints
            ? firstLiveScore?.points ?? 0
            : null;

const secondPoints = isFinal
    ? secondResult?.points ?? 0
    : canShowLivePoints
        ? secondLiveScore?.points ?? 0
        : null;

        const winnerId = String(
    game.result?.winnerId || ""
).trim();

const firstTeamWon =
    isFinal &&
    winnerId !== "" &&
    winnerId === firstTeamId;

const secondTeamWon =
    isFinal &&
    winnerId !== "" &&
    winnerId === secondTeamId;

/*
    Si el resultado final no tiene winnerId, se considera empate.
    Normalmente corresponde al caso de empate exacto.
*/
const isFinalTie =
    isFinal &&
    winnerId === "";

const firstPlayers = isProcessedBye
    ? game.result?.players ?? []
    : isFinal
        ? firstResult?.players ?? []
        : canShowLivePoints
            ? firstLiveScore?.players ?? []
            : [];

const secondPlayers = isFinal
    ? secondResult?.players ?? []
    : canShowLivePoints
        ? secondLiveScore?.players ?? []
        : [];

const gameStatus = isFinal
    ? "FINAL"
    : isBye
        ? "DESCANSA"
        : canShowLivePoints && hasLiveRealMatch
            ? "EN VIVO"
            : canShowLivePoints &&
              areAllRealMatchesFinal
                ? "PROCESANDO"
                : canShowLivePoints
                    ? "JORNADA EN CURSO"
                    : "PRÓXIMAMENTE";

                    const isPositionDecidingGame =
    playoffGame &&
    [
        "final",
        "third-place",
        "placement"
    ].includes(
        String(game.stage || "")
            .trim()
            .toLowerCase()
    );

return (
    <article
        className={[
            "fantasy-league-game",
            playoffGame
                ? "fantasy-league-game--playoff"
                : "",
            gameStatus === "EN VIVO"
                ? "fantasy-league-game--live"
                : ""
        ].join(" ")}

        key={game.id}
    >
        {playoffGame ? (
    <div className="fantasy-league-game__playoff-label">
        <IonIcon icon={trophyOutline} />
        <span>PLAYOFFS · {playoffGameLabel}</span>
    </div>
) : null}

        <div className="fantasy-league-game__status">
            {gameStatus}
        </div>

        <div className="fantasy-league-game__teams">
    <div
        className={
            firstTeamWon
                ? "fantasy-league-game__team fantasy-league-game__team--winner"
                : "fantasy-league-game__team"
        }
    >
        <div className="fantasy-league-game__team-name">
            <strong>
                {firstTeam?.name || "Equipo 1"}
            </strong>

            {firstTeamWon ? (
                <span
                    className={[
                        "fantasy-league-game__winner-tag",
                        isPositionDecidingGame
                            ? "fantasy-league-game__winner-tag--position"
                            : ""
                    ].join(" ")}
                >
                    <IonIcon icon={trophyOutline} />
                    {winnerTagLabel}
                </span>

            ) : null}

            {isFinalTie ? (
                <span className="fantasy-league-game__tie-tag">
                    EMPATE
                </span>
            ) : null}
        </div>

        <span>
            {firstPoints !== null
                ? `${firstPoints.toFixed(2)} PTS`
                : "—"}
        </span>
    </div>

    <b>{isBye ? "BYE" : "VS"}</b>

    <div
        className={
            secondTeamWon
                ? "fantasy-league-game__team fantasy-league-game__team--winner"
                : "fantasy-league-game__team"
        }
    >
        <div className="fantasy-league-game__team-name">
            <strong>
                {isBye
                    ? "Descansa"
                    : secondTeam?.name || "Equipo 2"}
            </strong>

            {secondTeamWon ? (
                <span
                    className={[
                        "fantasy-league-game__winner-tag",
                        isPositionDecidingGame
                            ? "fantasy-league-game__winner-tag--position"
                            : ""
                    ].join(" ")}
                >
                    <IonIcon icon={trophyOutline} />
                    {winnerTagLabel}
                </span>
            ) : null}

            {!isBye && isFinalTie ? (
                <span className="fantasy-league-game__tie-tag">
                    EMPATE
                </span>
            ) : null}
        </div>

        <span>
            {!isBye && secondPoints !== null
                ? `${secondPoints.toFixed(2)} PTS`
                : "—"}
        </span>
    </div>
</div>

        {canShowLivePoints ? (
            <small className="fantasy-league-game__updated">
                Actualizado:{" "}
                {liveRound?.updatedAt
                    ? new Intl.DateTimeFormat("es-MX", {
                          hour: "2-digit",
                          minute: "2-digit"
                      }).format(
                          new Date(liveRound.updatedAt)
                      )
                    : "ahora"}
            </small>
        ) : null}

        {isFinal &&
        game.result?.tieBreaker &&
        game.result.tieBreaker !== "points" ? (
            <small className="fantasy-league-game__tie">
                Desempate:{" "}
                {getTieBreakerLabel(
                    game.result.tieBreaker
                )}
            </small>
        ) : null}

        {!isBye &&
canShowPlayerPoints &&
(firstPlayers.length > 0 ||
    secondPlayers.length > 0) ? (

            <details className="fantasy-league-game__players">
                <summary>
                    Ver puntos de jugadores
                </summary>

                <div className="fantasy-league-game__players-grid">
                    <div>
                        <strong>
                            {firstTeam?.name || "Equipo 1"}
                        </strong>

                        {firstPlayers.map(function (player) {
                            return (
                                <div
                                    className="fantasy-league-player-score"
                                    key={player.id}
                                >
                                    <span>
                                        {player.name}
                                        {!player.isStarter
                                            ? " · BN"
                                            : ""}
                                    </span>

                                    <b>
                                        {Number(
                                            player.points || 0
                                        ).toFixed(2)}
                                    </b>
                                </div>
                            );
                        })}
                    </div>

                    <div>
                        <strong>
                            {secondTeam?.name || "Equipo 2"}
                        </strong>

                        {secondPlayers.map(function (player) {
                            return (
                                <div
                                    className="fantasy-league-player-score"
                                    key={player.id}
                                >
                                    <span>
                                        {player.name}
                                        {!player.isStarter
                                            ? " · BN"
                                            : ""}
                                    </span>

                                    <b>
                                        {Number(
                                            player.points || 0
                                        ).toFixed(2)}
                                    </b>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </details>
        ) : null}
    </article>
);

                    })}
                </div>
            )}
        </section>

        <section className="fantasy-league-section">
            <div className="fantasy-league-section__heading">
                <div>
                    <span>CLASIFICACIÓN</span>
                    <h2>Tabla de posiciones</h2>
                </div>

                <IonIcon icon={trophyOutline} />
            </div>

            {standings.length === 0 ? (
                <div className="fantasy-league-notice">
                    <span className="fantasy-league-notice__icon">
                        <IonIcon icon={trophyOutline} />
                    </span>

                    <div>
                        <strong>Aún no hay posiciones</strong>

                        <p>
                            La tabla aparecerá al procesar la primera
                            jornada Fantasy.
                        </p>
                    </div>
                </div>
            ) : (
                <>
                    <div className="fantasy-standings">
                        <div className="fantasy-standings__header">
                            <span>#</span>
                            <span>Equipo</span>
                            <span>G-P</span>
                            <span>PF</span>
                            <span>PC</span>
                        </div>

                        {standings.map(function (
                            standing: FantasyStanding
                        ) {
                            const team =
                                fantasyState?.teams[
                                    standing.teamId
                                ];

                            const isMyTeam =
                                standing.teamId ===
                                fantasyState?.league
                                    .currentUserTeamId;

                            return (
                                <div
                                    className={[
                                        "fantasy-standings__row",
                                        isMyTeam
                                            ? "fantasy-standings__row--mine"
                                            : ""
                                    ].join(" ")}
                                    key={standing.teamId}
                                >
                                    <strong>{standing.rank}</strong>

                                    <span>
                                        {team?.name || "Equipo"}

                                        {standing.playoffClinched === true && (
                                            <span
                                                className="fantasy-standings__clinched"
                                                aria-label="Clasificado matemáticamente a playoffs"
                                                title="Clasificado matemáticamente a playoffs"
                                            >
                                                {" "}*
                                            </span>
                                        )}
                                    </span>

                                    <span>
                                        {standing.wins}-
                                        {standing.losses}
                                        {standing.ties > 0
                                            ? `-${standing.ties}`
                                            : ""}
                                    </span>

                                    <span>
                                        {standing.pointsFor.toFixed(
                                            2
                                        )}
                                    </span>

                                    <span>
                                        {standing.pointsAgainst.toFixed(
                                            2
                                        )}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    <p className="fantasy-standings__rule">
                        * Clasificado matemáticamente a playoffs.
                        El orden se determina por victorias y los criterios
                        de desempate de la liga.
                    </p>
                </>
            )}
        </section>
        <section className="fantasy-league-section fantasy-league-activity">
    <div className="fantasy-league-section__heading">
        <div>
            <span>
                ACTIVIDAD DE LA JORNADA
            </span>

            <h2>
                Movimientos de la liga
            </h2>
        </div>

        <IonIcon icon={timeOutline} />
    </div>

    {leagueActivity.length === 0 ? (
        <div className="fantasy-league-notice">
            <span className="fantasy-league-notice__icon">
                <IonIcon icon={timeOutline} />
            </span>

            <div>
                <strong>
                    Aún no hay movimientos
                </strong>

                <p>
                    Las altas, bajas, waivers y trades de esta jornada
                    aparecerán aquí.
                </p>
            </div>
        </div>
    ) : (
        <div className="fantasy-league-activity__list">
            {leagueActivity.map(function (transaction) {
                return (
                    <article
                        key={transaction.id}
                        className="fantasy-league-activity__item"
                    >
                        <div className="fantasy-league-activity__content">
                            <span
                                className={[
                                    "fantasy-league-activity__type",
                                    `fantasy-league-activity__type--${transaction.type}`
                                ].join(" ")}
                            >
                                {getActivityTypeLabel(
                                    transaction.type
                                )}
                            </span>

                            <strong>
                                {getActivityDescription(
                                    transaction
                                )}
                            </strong>

                            <small>
                                {formatActivityDate(
                                    transaction.createdAt
                                )}
                            </small>
                        </div>
                    </article>
                );
            })}
        </div>
    )}
</section>

    </>
) : (
    <>
        <section className="fantasy-league-welcome">
            <span>ADMINISTRA TU TEMPORADA</span>
            <h2>Todo listo para competir</h2>

            <p>
                Revisa tu equipo, prepara tu draft y gestiona las
                opciones disponibles de la liga.
            </p>
        </section>

        <section className="fantasy-league-actions">
            <button
                className="fantasy-league-action fantasy-league-action--primary"
                type="button"
                onClick={function () {
                    router.push(
                        `/fantasyPlayers/${id}`,
                        "none",
                        "replace"
                    );
                }}
            >
                <span className="fantasy-league-action__icon">
                    <IonIcon icon={peopleOutline} />
                </span>

                <span className="fantasy-league-action__text">
                    <strong>Jugadores</strong>

                    <small>
                        Agrega, suelta y consulta líderes de la
                        jornada.
                    </small>
                </span>

                <IonIcon
                    className="fantasy-league-action__arrow"
                    icon={arrowForwardOutline}
                />
            </button>

            <button
                className="fantasy-league-action"
                type="button"
onClick={function () {
    const from = encodeURIComponent(
        location.pathname + location.search
    );

    router.push(
        `/fantasySettingsMenu/${id}?from=${from}`,
        "forward",
        "push"
    );
}}
            >
                <span className="fantasy-league-action__icon">
                    <IonIcon icon={settingsOutline} />
                </span>

                <span className="fantasy-league-action__text">
                    <strong>Configuración de liga</strong>

                    <small>
                        Revisa reglas, draft y ajustes de la
                        competencia.
                    </small>
                </span>

                <IonIcon
                    className="fantasy-league-action__arrow"
                    icon={arrowForwardOutline}
                />
            </button>
        </section>

        <section className="fantasy-league-section">
            <div className="fantasy-league-section__heading">
                <div>
                    <span>
    {isDraftPending
        ? "PRÓXIMO EVENTO"
        : "DRAFT ACTIVO"}
</span>

<h2>
    {isDraftPending
        ? "Actividad de la liga"
        : "Completa tus selecciones"}
</h2>

                </div>

                <IonIcon
    icon={
        isDraftPending
            ? calendarOutline
            : americanFootballOutline
    }
/>

            </div>

<div className="fantasy-draft-card">
    <div className="fantasy-draft-card__top">
        <span className="fantasy-draft-card__icon">
            <IonIcon icon={calendarOutline} />
        </span>

        <div>
            <span className="fantasy-draft-card__label">
                {isDraftPending
                    ? "DRAFT PROGRAMADO"
                    : "DRAFT EN CURSO"}
            </span>

            <strong>
                {isDraftPending
                    ? "Tu draft comienza pronto"
                    : "El draft está disponible"}
            </strong>

            {draftDate ? (
                <p>
                    {isDraftPending
                        ? formatDraftDate(draftDate)
                        : "El tiempo programado ya inició. Completa tus selecciones."}
                </p>
            ) : (
                <p>
                    Accede al draft para realizar o revisar tus
                    selecciones.
                </p>
            )}
        </div>
    </div>

    {isDraftPending ? (
        <div className="fantasy-draft-card__countdown">
            <IonIcon icon={timeOutline} />

            <div>
                <span>FALTAN</span>

                <strong>
                    {formatCountdown(
                        remainingDraftTime
                    )}
                </strong>
            </div>
        </div>
    ) : (
        <div className="fantasy-draft-card__countdown">
            <IonIcon icon={americanFootballOutline} />

            <div>
                <span>ESTADO</span>

                <strong>
                    Esperando selecciones
                </strong>
            </div>
        </div>
    )}

    <div className="fantasy-draft-card__details">
        {leagueInfo?.format ? (
            <span>
                Formato: {leagueInfo.format}
            </span>
        ) : null}

        {leagueInfo?.order ? (
            <span>
                Orden: {leagueInfo.order}
            </span>
        ) : null}

        {leagueInfo?.time ? (
            <span>
                {leagueInfo.time} s por selección
            </span>
        ) : null}
    </div>

    <IonButton
        className="fantasy-draft-card__button"
        expand="block"
        onClick={function () {
            router.push(
                `/fantasyDraft/${id}`,
                "forward",
                "push"
            );
        }}
    >
        {isDraftPending
            ? "Ir al draft"
            : "Continuar draft"}

        <IonIcon
            slot="end"
            icon={arrowForwardOutline}
        />
    </IonButton>
</div>

        </section>
    </>
)}
                    </main>
                )}
            </IonContent>
        </IonPage>
    );
};

export default FantasyLeague;
